/* @flow */

import { getCookie } from './cookie';
import { logDebug } from './logger';

/**
 * @module utils/ccpautils
 * @description CCPA Utility functions
 * @requires utils/cookie
 */

const USP_STRING_TTL_SEC: number = 30 * 60; // 30 min; CMP should call refreshPrivacySignals on change (COM-7697)
let USP_STRING_TTL: number = USP_STRING_TTL_SEC;
const CCPA_OPTED_OUT_STRING: string = '1YYN'; // LoggedIn + CCPA Opt-out
const CCPA_DEFAULT_STRING: string = '1---'; // Not logged in (state unknown)
const CCPA_VERSION: number = 1;

/** US Privacy Cookie key */
const CCPA_COOKIE_KEY: string = 'usprivacy';

/** CCPA Commands */
const CCPA_CMD_GET_USP_DATA: string = 'getUSPData';
/** CCPA Commands End */

const GDPR_COOKIE_KEY: string = 'u_consent';

/** local cached CCPA USP String */
let uspString: string = CCPA_DEFAULT_STRING;
/** last updated time for uspString */
let lastUspStringUpdated: number = 0;

/**
 * Get GDPR consent string
 * @returns {string} - GDPR consent value
 */
export function getGDPRConsent(): string {
  return getCookie(GDPR_COOKIE_KEY) || '';
}

/**
 * Check if from EU region
 * @returns {boolean} - From EU/not
 */
export function isFromEU(): boolean {
  return getGDPRConsent().length > 0;
}

/**
 * Validates USP string format per IAB specification
 * Format: 4 characters where:
 * - Position 1: Version (must be '1')
 * - Positions 2-4: 'Y', 'N', or '-' only
 *
 * @param {string} str - String to validate
 * @returns {boolean} - True if valid USP string format
 */
function isValidUSPString(str: string): boolean {
  if (!str || str.length !== 4) {
    return false;
  }
  // Position 1 must be '1' (version)
  if (str[0] !== '1') {
    return false;
  }
  // Positions 2-4 must be 'Y', 'N', or '-'
  const validChars = new Set(['Y', 'N', '-']);

  return (
    validChars.has(str[1]) && validChars.has(str[2]) && validChars.has(str[3])
  );
}

/**
 * Gets CCPA String
 * Cached locally; re-read from usprivacy after TTL expires or when ignoreTTL is true.
 * Call __as.refreshPrivacySignals() after CMP updates the cookie (COM-7697).
 *
 * @returns {string} - CCPA String
 */
export function getCCPA(ignoreTTL: boolean = false): string {
  if (isFromEU()) {
    // consider all traffic from EU as CCPA opted-out irrespective of the customer preference
    return CCPA_OPTED_OUT_STRING;
  }

  // Can we use the cached one?
  const curTime = Date.now() / 1000;
  if (
    !ignoreTTL &&
    lastUspStringUpdated > 0 &&
    curTime - lastUspStringUpdated <= USP_STRING_TTL
  ) {
    return uspString;
  }

  // fetch from cookie and update
  // In production: use window.document (global is undefined in browsers)
  // In test environments: check all available documents in priority order
  // Priority order: global.document (test) > window.document (production) > global.window.document (fallback)
  const docs = [
    typeof global === 'undefined' ? undefined : global?.document,
    window?.document,
    typeof global === 'undefined' ? undefined : global?.window?.document
  ]
    // eslint-disable-next-line unicorn/prefer-native-coercion-functions
    .filter(d => d) // Remove undefined/null
    .filter((d, i, arr) => arr.indexOf(d) === i); // Remove duplicates
  // $FlowFixMe[incompatible-call] - filter preserves Document type, but Flow infers boolean|Document

  // Find first document with valid cookie
  let cookieValue = '';
  docs.find(doc => {
    const value = getCookie(CCPA_COOKIE_KEY, doc);
    if (value && isValidUSPString(value)) {
      cookieValue = value;
      return true;
    }
    return false;
  });
  const newUspString =
    cookieValue && isValidUSPString(cookieValue)
      ? cookieValue
      : CCPA_DEFAULT_STRING;
  if (newUspString !== uspString) {
    logDebug(
      `CCPA UPDATE: uspString : ${uspString} -> ${newUspString}, lastUspStringUpdated : ${curTime}`
    );
  }
  uspString = newUspString;
  lastUspStringUpdated = curTime;
  return uspString;
}

/**
 * Gets CCPA sale/share restriction state (COM-7677 Accept-only gate).
 * Sale/share allowed only on explicit Accept: notice=Y and OptOutSale=N
 * (typically 1YNN). All other USP states (1YYN, 1Y--, 1---, missing) restrict.
 * getCCPA() still returns the raw cookie; EU traffic is forced to 1YYN there.
 * Ref: https://untdcorp.atlassian.net/wiki/spaces/COM/pages/4090888193/CCPA+update+2026+Dev+Design
 * Ref: https://github.com/InteractiveAdvertisingBureau/USPrivacy/blob/master/CCPA/US%20Privacy%20String.md
 *
 * @returns {boolean} - true when sale/share must be restricted
 */
export function isCCPAOptedOut(): boolean {
  const usp = getCCPA();
  // Accept only: Version+Notice Y + OptOutSale N (e.g. 1YNN / 1YNY)
  const isAccepted =
    typeof usp === 'string' &&
    usp.length === 4 &&
    usp[1] === 'Y' &&
    usp[2] === 'N';
  return !isAccepted;
}

/**
 * Set USP string TTL for testing purposes
 * @param {number} ttl - TTL in seconds
 * @returns {undefined}
 */
export function setUSPStringTTL(ttl: number): void {
  USP_STRING_TTL = ttl;
}

window.__uspapi =
  window.__uspapi ||
  function __uspapi(command: string, version: number, callback: uspCallback) {
    if (command === CCPA_CMD_GET_USP_DATA) {
      // Validate version parameter if provided
      if (version !== undefined && version !== CCPA_VERSION) {
        // Unsupported version - return error per IAB USP-API spec
        callback(null, false);
        return;
      }
      /* Get CCPA Cookie value */
      const ccpaValue = getCCPA();
      /* Call callback with cookie value & CCPA version */
      callback(
        {
          version: CCPA_VERSION,
          uspString: ccpaValue
        },
        true
      );
    } else {
      // Unknown command - return error per IAB USP-API spec
      callback(null, false);
    }
  };
